import { Box, Dialog, DialogTitle, Typography, useMediaQuery } from '@material-ui/core';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { Button } from 'components/UI';
import { Fragment, FunctionComponent } from 'react';

export interface Props {
    open: boolean;
    title?: JSX.Element | string;
    question?: JSX.Element | string;
    subquestion?: JSX.Element | string;
    actions?: JSX.Element;
    isSuccess?: boolean;
    className?: string;
    cancelButtonLabel?: string;
    confirmButtonLabel?: string;
    disabled?: boolean;
    processing?: boolean;
    onCancel?: () => void;
    onConfirm?: () => void;
    id?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        dialog: {
            '& .MuiDialog-paper': {
                [theme.breakpoints.down('sm')]: {
                    width: '100%',
                    height: '100%',
                    maxHeight: '100%',
                    maxWidth: '100% !important',
                    borderRadius: 0,
                    margin: 0
                },
                boxShadow: 'none',
                padding: 10,
                minWidth: 429,
                minHeight: 264,
                borderRadius: 24
            },
            '& .MuiDialogContent-root': {
                padding: 0,
                display: 'flex',
                margin: '0px auto',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center',
                width: '100%'
            },
            '& .MuiDialogActions-root': {
                gap: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '17px 30px'
            }
        },
        question: {
            fontSize: 18,
            fontWeight: 400
        },
        space: {
            marginBottom: 15
        },
        subquestion: {
            color: '#000',
            fontWeight: 400,
            fontSize: 16
        },
        button: {
            height: 47,
            width: 175,
            borderRadius: 31
        },
        titleContainer: {
            '&.MuiDialogTitle-root': {
                '& .MuiTypography-h6': {
                    [theme.breakpoints.down('sm')]: {
                        justifyContent: 'flex-end'
                    },
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    padding: '0px 24px !important',
                    marginTop: 20
                }
            }
        },
        iconClose: {
            fontSize: 24,
            lineHeight: '38px',
            float: 'right',
            cursor: 'pointer'
        }
    })
);

export const SendReceiptDialog: FunctionComponent<Props> = props => {
    const classes = useStyles(props);

    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Dialog
            open={props.open}
            className={clsx(classes.dialog, props.className)}
            onClose={() => props.onCancel && props.onCancel()}
        >
            <DialogTitle className={classes.titleContainer}>
                {!isMobile && props.title}
                <FontAwesomeIcon
                    icon={faTimes}
                    className={classes.iconClose}
                    onClick={props.onCancel}
                />
            </DialogTitle>

            <DialogContent>
                {isMobile && props.title}
                {props.question && (
                    <Typography
                        className={clsx(classes.question, {
                            [classes.space]: props.subquestion
                        })}
                    >
                        {typeof props.question === 'string' ? (
                            <Typography>{props.question}</Typography>
                        ) : (
                            props.question
                        )}
                    </Typography>
                )}

                {props.subquestion && (
                    <Typography className={clsx(classes.subquestion)}>
                        {typeof props.subquestion === 'string' ? (
                            <Typography>{props.subquestion}</Typography>
                        ) : (
                            props.subquestion
                        )}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions>
                <Fragment>
                    {props.cancelButtonLabel && (
                        <Button
                            id={
                                props.id &&
                                props.id.concat(props.cancelButtonLabel?.toLowerCase() || '').concat('_button')
                            }
                            label={props.cancelButtonLabel}
                            onClick={event => {
                                event.stopPropagation();
                                props.onCancel && props.onCancel();
                            }}
                            color="black"
                            className={classes.button}
                            disabled={props.processing}
                        />
                    )}
                    {props.confirmButtonLabel && (
                        <Button
                            id={
                                props.id &&
                                props.id.concat(props.confirmButtonLabel?.toLowerCase() || '').concat('_button')
                            }
                            label={props.confirmButtonLabel}
                            onClick={event => {
                                event.stopPropagation();
                                props.onConfirm && props.onConfirm();
                            }}
                            variant={props.isSuccess ? 'primary' : 'danger'}
                            color={props.isSuccess ? 'green' : 'orange'}
                            className={classes.button}
                            loading={props.processing}
                            disabled={props.disabled}
                        />
                    )}
                </Fragment>
            </DialogActions>
        </Dialog>
    );
};

export default SendReceiptDialog;
